<template>
  <div class="relative">
    <div
      id="formula-editor"
      ref="editor"
      class="relative min-h-14 rounded-sm border p-3"
      :class="[
        hasErrors ? 'border-error' : 'border-blue-200',
        { 'pt-4': isEditorEmpty && isEditorFocused },
        'flex flex-wrap items-center',
        { 'gap-y-2': !isPrettyMode },
        { 'space-x-0-25': !hasWhitespaceNodes && !isPrettyMode },
      ]"
      :contenteditable="!disabled"
      spellcheck="false"
      data-cy="formula-editor"
      @paste.prevent="handlePaste"
      @copy.prevent="copyContent"
      @keydown.shift.exact.stop="handleInput"
      @keydown.ctrl.exact.stop="handleShortcuts"
      @keydown.meta.exact.stop="handleShortcuts"
      @keydown.exact="handleInput"
      @blur="handleBlur"
      @focusin="emit('focus')"
    >
      <editor-node-component
        v-for="(node, index) in editorContent"
        ref="componentRefs"
        :key="node.id"
        v-model="editorContent[index]"
        :previous-node="editorContent[index - 1]"
        :index="index"
        :is-pretty-mode="isPrettyMode"
        :disabled="disabled"
      />
    </div>
    <teleport to="#modals">
      <formula-dropdown
        v-show="
          !isEditorEmpty &&
          [
            EditorNodeName.uncategorized,
            EditorNodeName.unrecognized,
            EditorNodeName.attributeOption,
          ].includes(computedCurrentNode?.type)
        "
        class="absolute z-90 max-h-72 w-92 overflow-y-auto rounded-sm bg-white p-2 shadow-lg"
        :style="getDropdownPosition()"
        :node="computedCurrentNode"
        :argument-options="currentArgumentOptions"
        @node:select="applySelection($event, computedCurrentNodeIndex)"
      />
    </teleport>
    <div
      v-if="showPlaceholder"
      class="pointer-events-none absolute top-4 left-4"
      data-cy="placeholder"
    >
      {{ placeholder }}
    </div>
  </div>
</template>
<script setup lang="ts">
import { useFocus } from "@vueuse/core";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { AllowedKey, EditorNodeName } from "@/enums/customAttributes";
import { getServiceAttributes } from "@/helpers/common";
import {
  compactFormula,
  formatFormula,
  hasWhitespaceFormatting,
  isWhitespaceNode,
} from "@/helpers/parser/formatter";
import { useFormulaEditor } from "@/hooks/attributeBuilder";
import {
  SelectionDirection,
  createEditorComposables,
  useEditorClipboard,
  useEditorCore,
  useEditorDropdown,
  useEditorFunctionOptions,
  useEditorHistory,
  useEditorShortcuts,
} from "@/hooks/formulaEditorCore";
import { useAttributeBuilderStore } from "@/stores/customAttributeBuilder";

import EditorNodeComponent from "@/components/customAttributesV2/EditorNode.vue";
import FormulaDropdown from "@/components/customAttributesV2/FormulaDropdown.vue";

import type { EditorNode } from "@/models/TextEditor";
import type { CustomAttributeDropdownOption } from "@/models/customAttributes/searchSelectDropdown";
import type { FormulaOption } from "@/models/options";
import type { IAttribute } from "@/models/orchestration";
import type { PropType } from "vue";

interface LocalFunctionDefinition {
  definition?: FormulaOption;
  arguments: EditorNode[][];
  optionsSet: Record<string, string | undefined>;
}

const props = defineProps({
  modelValue: {
    type: Array as PropType<EditorNode[]>,
    required: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const isPrettyMode = defineModel<boolean>("isPrettyMode", { default: false });

const { t } = useI18n();
const formulaEditor = useFormulaEditor();
const { createEditorNode, functionsByNameMap } = formulaEditor;

const { services } = storeToRefs(useAttributeBuilderStore());
const serviceAttributes = computed(() =>
  services.value.reduce<IAttribute[]>(getServiceAttributes, [])
);

const { getters, dispatch } = useStore();

const emit = defineEmits<{
  "update:model-value": [EditorNode[]];
  focus: [];
}>();

const editor = ref<HTMLElement | null>(null);
const { focused: isEditorFocused } = useFocus(editor);
const componentRefs = ref<HTMLElement[]>([]);
const editorContent = ref<EditorNode[]>([]);

const isProcessingKeyInput = ref(false);
const pendingTimeouts = ref<Set<number>>(new Set());
const isComponentMounted = ref(false);

const editorCore = useEditorCore(editorContent, componentRefs);
const {
  selectionRange,
  isUpdatingRange,
  currentNodeIndex,
  currentNode,
  handleSelectionChange: coreHandleSelectionChange,
} = editorCore;
const localFunctionDefinition = ref<LocalFunctionDefinition>({
  arguments: [],
  optionsSet: {},
});

const hasErrors = computed(() =>
  editorContent.value.some((node) => node.error?.message)
);

const computedCurrentNodeIndex = computed(() => {
  return currentNodeIndex.value;
});

const computedCurrentNode = computed(() => {
  return currentNode.value;
});

const isEditorEmpty = computed(
  () => editorContent.value.length === 0 || !editorContent.value[0].content
);

const hasWhitespaceNodes = computed(() =>
  editorContent.value.some(isWhitespaceNode)
);

const showPlaceholder = computed(
  () => !isEditorFocused.value && isEditorEmpty.value && !!props.placeholder
);

const attributeOptions = computed(() => getters["options/attributeOptions"]);

const currentArgumentOptions = computed(() => {
  if (!computedCurrentNode.value?.optionsKey) {
    return {};
  }
  return attributeOptions.value[computedCurrentNode.value.optionsKey];
});

const editorFunctionOptions = useEditorFunctionOptions(
  localFunctionDefinition,
  attributeOptions,
  dispatch
);

const setLocalFunctionDefinition = (functionName: string) => {
  localFunctionDefinition.value = {
    definition: functionsByNameMap.value[functionName.toUpperCase()],
    arguments: editorBrackets.getFunctionArgumentNodes(),
    optionsSet: {},
  };
  editorFunctionOptions.setFunctionNodeOptions();
};

const editorComposables = createEditorComposables({
  editorContent,
  core: editorCore,
  functionsByNameMap,
  serviceAttributes,
  isProcessingKeyInput,
  createEditorNode,
  currentNodeIndex,
  setLocalFunctionDefinition,
  emit,
});

const {
  content: editorContentUpdater,
  input: editorInput,
  brackets: editorBrackets,
} = editorComposables;

const editorDropdown = useEditorDropdown(editor, editorCore);

const editorHistory = useEditorHistory(
  editorContent,
  editorCore,
  createEditorNode
);

const getDropdownPosition = () => {
  return editorDropdown.getDropdownPosition(computedCurrentNodeIndex.value);
};

const recordEditorState = editorHistory.recordEditorState;
const updateEditorContent = editorComposables.content.updateContent;

const processBackspace = async (): Promise<void> => {
  if (editorContentUpdater.shouldDeleteAsRange()) {
    await editorContentUpdater.deleteSelectedRange();
  } else {
    await editorContentUpdater.deleteSingleCharacter(currentNodeIndex.value);
  }
};

const updateSelectionRange = editorCore.updateCursorPosition;
const handleArrowKey = editorInput.processArrowKeyNavigation;
const handleBackspaceKey = processBackspace;
const handleCharacterKey = editorInput.processCharacterInput;
const resetActiveBrackets = editorBrackets.resetActiveBrackets;
const updateActiveBracketPairRange =
  editorBrackets.updateActiveBracketPairRange;

const editorClipboard = useEditorClipboard(
  editorContent,
  selectionRange,
  handleCharacterKey,
  recordEditorState
);

const { handleShortcuts } = useEditorShortcuts(
  editorCore,
  editorContent,
  editorClipboard,
  editorHistory
);

const handlePaste = editorClipboard.handlePaste;

const handleInput = async (event: KeyboardEvent) => {
  const key = event.key;
  if (!editor.value) {
    return;
  }
  event.preventDefault();

  if (key === AllowedKey.arrowLeft || key === AllowedKey.arrowRight) {
    await handleArrowKey(key);
  } else if (key === AllowedKey.backspace) {
    await handleBackspaceKey();
  } else if (key.length === 1) {
    await handleCharacterKey(key);
  }
  if (
    key === AllowedKey.arrowLeft ||
    key === AllowedKey.arrowRight ||
    key === AllowedKey.shift
  ) {
    return;
  }
  await recordEditorState();
  updateActiveBracketPairRange();
};

const handleSelectionChange = () => {
  if (!editor.value) {
    return;
  }

  coreHandleSelectionChange();
  updateActiveBracketPairRange();
};

watch(
  () => props.modelValue,
  (newValue) => {
    const content = newValue.length
      ? newValue
      : [createEditorNode("", EditorNodeName.uncategorized)];
    editorContent.value = content;
    isPrettyMode.value = hasWhitespaceFormatting(content);
  }
);

watch(isPrettyMode, (newValue, oldValue) => {
  if (oldValue === undefined) {
    return;
  }

  const hasFormatting = hasWhitespaceFormatting(editorContent.value);

  if (newValue && !hasFormatting) {
    const formattedContent = formatFormula(editorContent.value);
    const formattingProducedWhitespace =
      hasWhitespaceFormatting(formattedContent);

    if (!formattingProducedWhitespace) {
      isPrettyMode.value = false;
      return;
    }

    editorContent.value = formattedContent;
    emit("update:model-value", editorContent.value);
  } else if (!newValue && hasFormatting) {
    editorContent.value = compactFormula(editorContent.value);
    emit("update:model-value", editorContent.value);
  }
});

const applySelection = async (
  node: CustomAttributeDropdownOption,
  positionIndex: number
) => {
  if (!node.type) {
    return;
  }
  const nodeContent =
    node.type === EditorNodeName.attributeOption ? `'${node.name}'` : node.name;
  const newNode = createEditorNode(nodeContent, node.type);
  if (node.type === EditorNodeName.attribute) {
    newNode.attributeId = node.id;
    newNode.context = node.context;
  }

  if (node.type === EditorNodeName.attributeOption) {
    newNode.context = { tooltip: node.label ?? "" };
  }
  await updateEditorContent(positionIndex, positionIndex + 1, [newNode]);
  const selection = window.getSelection();
  if (!selection) {
    return;
  }
  await updateSelectionRange(positionIndex, nodeContent.length);
  await recordEditorState();
};

const copyContent = async () => {
  await editorClipboard.copyContent();
};

const convertOrRemoveUncategorizedNodes = () => {
  const hasUncategorized = editorContent.value.some(
    (node) => node.type === EditorNodeName.uncategorized
  );

  if (!hasUncategorized) {
    return;
  }

  const newEditorContent = editorContent.value.reduce<EditorNode[]>(
    (acc, node) => {
      if (node.type !== EditorNodeName.uncategorized) {
        acc.push(node);
      } else if (node.content?.trim()) {
        acc.push({
          ...node,
          type: EditorNodeName.unrecognized,
          error: {
            message: t("ATTRIBUTE_BUILDER.UNRECOGNIZED_CONTENT", {
              content: node.content,
            }),
          },
        });
      }
      return acc;
    },
    []
  );

  editorContent.value = newEditorContent;
  emit("update:model-value", newEditorContent);
};

const handleBlur = () => {
  resetActiveBrackets();
  convertOrRemoveUncategorizedNodes();
};

const insertAttribute = async (attribute: IAttribute) => {
  const option: CustomAttributeDropdownOption = {
    name: attribute.name,
    id: attribute.id,
    type: EditorNodeName.attribute,
    context: attribute.context,
  };

  const range = selectionRange.value;
  const hasSelection =
    range &&
    (range.start !== range.end || range.startOffset !== range.endOffset);

  if (hasSelection && range) {
    const startIndex = range.start;
    const endIndex = range.end + 1;
    const newNode = createEditorNode(option.name, EditorNodeName.attribute);
    newNode.attributeId = option.id;
    newNode.context = option.context;
    await updateEditorContent(startIndex, endIndex, [newNode]);
    await updateSelectionRange(startIndex, option.name.length);
  } else {
    const endIndex = editorContent.value.length - 1;
    const lastNode = editorContent.value[endIndex];

    if (
      lastNode?.type === EditorNodeName.uncategorized &&
      !lastNode.content?.trim()
    ) {
      await applySelection(option, endIndex);
    } else {
      const newNode = createEditorNode(option.name, EditorNodeName.attribute);
      newNode.attributeId = option.id;
      newNode.context = option.context;
      await updateEditorContent(endIndex + 1, endIndex + 1, [newNode]);
      await updateSelectionRange(endIndex + 1, option.name.length);
    }
  }

  editor.value?.focus();
  await recordEditorState();
};

onMounted(async () => {
  isComponentMounted.value = true;
  document.addEventListener("selectionchange", handleSelectionChange);
  const content = props.modelValue.length
    ? props.modelValue
    : [createEditorNode("", EditorNodeName.uncategorized)];
  editorContent.value = content;
  isPrettyMode.value = hasWhitespaceFormatting(content);
  const offset = content[content.length - 1]?.content?.length ?? 0;
  selectionRange.value = {
    start: content.length - 1,
    startOffset: offset,
    end: content.length - 1,
    endOffset: offset,
    selectionDirection: SelectionDirection.ltr,
  };
  if (
    content.length === 1 &&
    content[0].type === EditorNodeName.uncategorized
  ) {
    return;
  }
  await recordEditorState();
});

onBeforeUnmount(() => {
  isComponentMounted.value = false;

  document.removeEventListener("selectionchange", handleSelectionChange);

  pendingTimeouts.value.forEach((timeoutId) => {
    clearTimeout(timeoutId);
  });
  pendingTimeouts.value.clear();

  isProcessingKeyInput.value = false;
  isUpdatingRange.value = false;
});

defineExpose({ insertAttribute });
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.editor {
  border: 1px solid #ccc;
  padding: 10px;
  min-height: 100px;
}
</style>
